import React, { useState, useEffect, useRef } from 'react';
import { X, Calendar as CalendarIcon, Users, MessageSquare, Send, CheckCircle2, Phone, Mail, Building, MapPin, Check, ChevronDown, ChevronUp, Search } from 'lucide-react';
import './AgencyEnquiryModal.css';
import API_BASE_URL from '../config';

interface Agency {
    _id: string;
    name: string;
    email: string;
    phone?: string;
}

interface AgencyEnquiryModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const AgencyEnquiryModal: React.FC<AgencyEnquiryModalProps> = ({ isOpen, onClose }) => {
    const [agencies, setAgencies] = useState<Agency[]>([]);
    const [selectedAgencyIds, setSelectedAgencyIds] = useState<string[]>([]);
    const [clientName, setClientName] = useState('');
    const [primaryMobile, setPrimaryMobile] = useState('');
    const [email, setEmail] = useState('');
    const [weddingDate, setWeddingDate] = useState('');
    const [city, setCity] = useState('');
    const [expectedGuests, setExpectedGuests] = useState(100);
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetchingAgencies, setFetchingAgencies] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Custom Dropdown & Search State
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fetch active agencies on mount/open
    useEffect(() => {
        if (isOpen) {
            setFetchingAgencies(true);
            setError('');
            fetch(`${API_BASE_URL}/api/public/agencies`)
                .then(res => res.json())
                .then(data => {
                    if (data.success) {
                        setAgencies(data.agencies);
                        // Default to selecting all agencies
                        setSelectedAgencyIds(data.agencies.map((a: Agency) => a._id));
                    } else {
                        setError('Failed to load agencies list.');
                    }
                })
                .catch(() => {
                    setError('Error connecting to the server.');
                })
                .finally(() => {
                    setFetchingAgencies(false);
                });

            // Reset form fields
            setClientName('');
            setPrimaryMobile('');
            setEmail('');
            setWeddingDate('');
            setCity('');
            setExpectedGuests(100);
            setNotes('');
            setSuccess(false);
            setIsDropdownOpen(false);
            setSearchQuery('');
        }
    }, [isOpen]);

    // Handle clicking outside the dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const toggleAgencySelection = (id: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedAgencyIds(prev => 
            prev.includes(id) 
                ? prev.filter(agencyId => agencyId !== id) 
                : [...prev, id]
        );
    };

    const removeAgencyTag = (id: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedAgencyIds(prev => prev.filter(agencyId => agencyId !== id));
    };

    const selectAllAgencies = (event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedAgencyIds(agencies.map(a => a._id));
    };

    const clearAllAgencies = (event: React.MouseEvent) => {
        event.stopPropagation();
        setSelectedAgencyIds([]);
    };

    // Filter agencies based on search query
    const filteredAgencies = agencies.filter(agency => 
        agency.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (selectedAgencyIds.length === 0) {
            setError('Please select at least one event management agency.');
            return;
        }

        const phoneRegex = /^[6-9]\d{9}$/;
        if (!phoneRegex.test(primaryMobile.replace(/\s+/g, ''))) {
            setError('Please enter a valid 10-digit mobile number.');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/api/public/agency/enquiry/bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    agencyIds: selectedAgencyIds,
                    clientName,
                    primaryMobile,
                    email,
                    weddingDate,
                    city,
                    expectedGuests,
                    notes
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to submit enquiry');
            }

            setSuccess(true);
            setTimeout(() => {
                setSuccess(false);
                onClose();
            }, 3000);
        } catch (err: any) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="agency-enquiry-modal-overlay">
            <div className="agency-enquiry-modal-content premium-shadow light-theme">
                <button className="modal-close-btn" onClick={onClose} aria-label="Close modal"><X /></button>
                
                {success ? (
                    <div className="modal-success-view">
                        <div className="success-icon-wrapper pulse-glow">
                            <CheckCircle2 size={70} color="#3E1F24" className="scale-up" />
                        </div>
                        <h2>Enquiry Broadcasted!</h2>
                        <p>Your enquiry has been successfully shared with the selected agencies. They will review your preferences and get in touch shortly.</p>
                    </div>
                ) : (
                    <div className="modal-form-view">
                        <div className="modal-header">
                            <div className="modal-logo-area">
                                <img src="/shadigram-logo-bold.png" alt="Shadi Gram Logo" className="modal-brand-logo" />
                            </div>
                            <h2 className="modal-colorful-heading">Elite Concierge</h2>
                        </div>

                        {error && <div className="modal-error-banner">{error}</div>}

                        <form onSubmit={handleSubmit} className="agency-enquiry-form">
                            
                            {/* Searchable Multi-Select Dropdown */}
                            <div className="form-group" ref={dropdownRef}>
                                <label><Building size={14} /> Select Event Agencies ({selectedAgencyIds.length} Selected)</label>
                                
                                <div className="custom-dropdown-container">
                                    {/* Dropdown Header Trigger */}
                                    <div 
                                        className={`dropdown-trigger-field ${isDropdownOpen ? 'active' : ''}`}
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    >
                                        <div className="selected-tags-display">
                                            {selectedAgencyIds.length === 0 ? (
                                                <span className="dropdown-placeholder">Select Event Agencies...</span>
                                            ) : selectedAgencyIds.length === agencies.length ? (
                                                <span className="dropdown-summary-tag">All Agencies Selected</span>
                                            ) : (
                                                <div className="tags-wrapper">
                                                    {selectedAgencyIds.slice(0, 3).map(id => {
                                                        const agency = agencies.find(a => a._id === id);
                                                        return agency ? (
                                                            <span key={id} className="selected-tag-pill" onClick={(e) => removeAgencyTag(id, e)}>
                                                                {agency.name}
                                                                <X size={10} className="tag-remove-icon" />
                                                            </span>
                                                        ) : null;
                                                    })}
                                                    {selectedAgencyIds.length > 3 && (
                                                        <span className="selected-tag-pill summary">
                                                            +{selectedAgencyIds.length - 3} more
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="dropdown-trigger-icons">
                                            {selectedAgencyIds.length > 0 && (
                                                <button type="button" onClick={clearAllAgencies} className="clear-selections-btn" title="Clear All">
                                                    <X size={14} />
                                                </button>
                                            )}
                                            {isDropdownOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                        </div>
                                    </div>

                                    {/* Dropdown Panel */}
                                    {isDropdownOpen && (
                                        <div className="dropdown-list-panel shadow-luxury">
                                            <div className="dropdown-search-box">
                                                <Search size={14} className="search-box-icon" />
                                                <input 
                                                    type="text" 
                                                    placeholder="Search planners by name..." 
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>

                                            <div className="dropdown-bulk-actions">
                                                <button type="button" onClick={selectAllAgencies} className="bulk-link-btn">Select All</button>
                                                <span className="bulk-bullet-sep">•</span>
                                                <button type="button" onClick={clearAllAgencies} className="bulk-link-btn">Clear All</button>
                                            </div>

                                            <div className="dropdown-items-list">
                                                {fetchingAgencies ? (
                                                    <div className="panel-loading-state">
                                                        <div className="spinner-lite"></div>
                                                        <span>Loading agencies...</span>
                                                    </div>
                                                ) : filteredAgencies.length === 0 ? (
                                                    <div className="panel-empty-state">No matching agencies found.</div>
                                                ) : (
                                                    filteredAgencies.map(agency => {
                                                        const isSelected = selectedAgencyIds.includes(agency._id);
                                                        return (
                                                            <div 
                                                                key={agency._id}
                                                                className={`dropdown-list-item ${isSelected ? 'selected' : ''}`}
                                                                onClick={(e) => toggleAgencySelection(agency._id, e)}
                                                            >
                                                                <div className="item-checkbox">
                                                                    {isSelected && <Check size={12} color="#fff" />}
                                                                </div>
                                                                <span className="item-name">{agency.name}</span>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="client-name">Your Name</label>
                                    <input 
                                        type="text" 
                                        id="client-name"
                                        placeholder="Full Name"
                                        required 
                                        value={clientName} 
                                        onChange={(e) => setClientName(e.target.value)} 
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="client-phone"><Phone size={14} /> Mobile Number</label>
                                    <input 
                                        type="tel" 
                                        id="client-phone"
                                        placeholder="10-digit mobile number"
                                        required 
                                        value={primaryMobile} 
                                        onChange={(e) => setPrimaryMobile(e.target.value)} 
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="client-email"><Mail size={14} /> Email Address (Optional)</label>
                                    <input 
                                        type="email" 
                                        id="client-email"
                                        placeholder="name@example.com"
                                        value={email} 
                                        onChange={(e) => setEmail(e.target.value)} 
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="wedding-date"><CalendarIcon size={14} /> Wedding Date</label>
                                    <input 
                                        type="date" 
                                        id="wedding-date"
                                        required 
                                        value={weddingDate} 
                                        onChange={(e) => setWeddingDate(e.target.value)} 
                                        min={new Date().toISOString().split('T')[0]}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="wedding-city"><MapPin size={14} /> City</label>
                                    <input 
                                        type="text" 
                                        id="wedding-city"
                                        placeholder="Wedding location city"
                                        required 
                                        value={city} 
                                        onChange={(e) => setCity(e.target.value)} 
                                    />
                                </div>
                                <div className="form-group">
                                    <div className="label-with-value">
                                        <label><Users size={14} /> Expected Guests</label>
                                        <span className="guest-badge">{expectedGuests} Guests</span>
                                    </div>
                                    <div className="range-container">
                                        <input 
                                            type="range" 
                                            min="50" 
                                            max="2000" 
                                            step="50"
                                            value={expectedGuests}
                                            onChange={(e) => setExpectedGuests(parseInt(e.target.value))}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="client-notes"><MessageSquare size={14} /> Special Requirements / Notes</label>
                                <textarea 
                                    id="client-notes"
                                    placeholder="Describe your vision, budget expectations, venue preferences, themes, or custom requests..."
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={3}
                                />
                            </div>

                            <button type="submit" className="modal-submit-btn-premium" disabled={loading || fetchingAgencies || selectedAgencyIds.length === 0}>
                                {loading ? 'Sending requests...' : <>Submit Enquiry to selected Agencies <Send size={16} /></>}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AgencyEnquiryModal;
